// apps/web/src/pages/auth/SignUp.tsx
//
// Plan 04-12b Task 1 — sign-up form.
//
// FIELDS (CONTEXT D-21 + plan must_haves):
//   - email (required, valid email)
//   - password (required, min 8 — server enforces; client also nudges)
//   - confirmPassword (required, must match)
//   - invitationToken (optional — preserved for post-verification redemption)
//
// FLOW:
//   1. User submits → call authClient.signUp.email({email, password})
//   2. On success → redirect to /auth/verify-email/pending so the user knows
//      to check their inbox
//   3. On error → display the server's message inline via Alert
//
// INVITATION TOKEN HANDLING (CONTEXT D-09 at-signup path):
//   The invitation token is captured at signup time but NOT redeemed until
//   after email verification + MFA enrollment (CONTEXT D-10 sequence).
//   For now we stash it in sessionStorage keyed by the user's email; the
//   /account/redeem-invite page can pick it up after the user signs in.
//   This is NOT auth state — just a workflow continuation hint. The token
//   itself only confers authorship after the server validates it via
//   POST /api/auth/invitations/redeem (Plan 04-11).

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";

import { authClient } from "@/lib/auth-client";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { EmailField, PasswordField } from "@/components/forms/EmailPasswordFields";
import { PasswordStrengthIndicator } from "@/components/forms/PasswordStrengthIndicator";

const signUpSchema = z
  .object({
    email: z.string().min(1, "Email is required").email("Invalid email"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
    invitationToken: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords do not match",
  });

type SignUpFormValues = z.infer<typeof signUpSchema>;

export function SignUp(): React.ReactElement {
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      invitationToken: "",
    },
    mode: "onSubmit",
  });

  // useWatch (NOT watch) per STACK.md note for React 19 + Compiler reliability.
  const passwordValue = useWatch({
    control: form.control,
    name: "password",
  }) as string | undefined;

  async function onSubmit(values: SignUpFormValues): Promise<void> {
    setServerError(null);
    try {
      const result = (await authClient.signUp.email({
        email: values.email,
        password: values.password,
        name: values.email,
      })) as {
        data: { user: { id: string; email: string } } | null;
        error: { message?: string; code?: string } | null;
      };
      if (result.error !== null && result.error !== undefined) {
        setServerError(
          result.error.message ?? "Could not create your account; please try again",
        );
        return;
      }

      // Stash the invitation token for the post-verification redemption flow.
      // sessionStorage (not localStorage) — auth tokens NEVER persist past tab
      // close; the invitation token is a workflow hint, not a credential.
      if (
        values.invitationToken !== undefined &&
        values.invitationToken.length > 0 &&
        typeof window !== "undefined" &&
        window.sessionStorage
      ) {
        try {
          window.sessionStorage.setItem(
            `pending-invitation:${values.email.toLowerCase()}`,
            values.invitationToken,
          );
        } catch {
          // SessionStorage may be unavailable in private-browsing mode. The
          // user can still manually paste their token at /account/redeem-invite
          // after verifying their email.
        }
      }

      navigate("/auth/verify-email/pending");
    } catch (err) {
      setServerError(
        err instanceof Error ? err.message : "Could not create your account",
      );
    }
  }

  return (
    <main className="mx-auto max-w-md p-8">
      <Card>
        <CardHeader>
          <CardTitle>Create an account</CardTitle>
          <CardDescription>
            Sign up to view recipes. Authoring requires an invitation.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
              noValidate
            >
              {serverError !== null ? (
                <Alert variant="destructive">
                  <AlertTitle>Sign-up failed</AlertTitle>
                  <AlertDescription>{serverError}</AlertDescription>
                </Alert>
              ) : null}

              <EmailField
                control={form.control}
                name="email"
                autoComplete="email"
                autoFocus
                disabled={form.formState.isSubmitting}
              />

              <div className="space-y-2">
                <PasswordField
                  control={form.control}
                  name="password"
                  label="Password"
                  autoComplete="new-password"
                  disabled={form.formState.isSubmitting}
                />
                <PasswordStrengthIndicator password={passwordValue ?? ""} />
              </div>

              <PasswordField
                control={form.control}
                name="confirmPassword"
                label="Confirm password"
                autoComplete="new-password"
                disabled={form.formState.isSubmitting}
              />

              <FormField
                control={form.control}
                name="invitationToken"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Invitation token (optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder="Paste your invitation token"
                        disabled={form.formState.isSubmitting}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      If you have an invitation, paste it here to gain authoring
                      access after verification.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? "Creating account…" : "Create account"}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/auth/sign-in" className="font-medium underline">
              Sign in
            </Link>
          </p>
        </CardFooter>
      </Card>
    </main>
  );
}
